import { useState, useEffect, useRef } from 'react';
import fetchAPI from 'app/helpers/fetch';

export type Advert = {
  id: string;
  first_appearance: number;
  next_appearance: number;
  body: string | null;
  title: string | null;
  action_text: string | null;
  action_url: string | null;
  tip: string | null;
  image_url: string | null;
};

export type AdvertResponse = {
  data: { id: Advert['id']; attributes: Omit<Advert, 'id'> }[];
};

const getStoredAdvert = () => {
  const adv = localStorage.getItem('advert');
  return adv ? (JSON.parse(adv) as Advert) : undefined;
};

const blocked = () => !!localStorage.getItem('advertBlocked');

type UseAdverts = () => { active: boolean; advert: Advert | undefined; accept: () => void; close: () => void };

// eslint-disable-next-line max-lines-per-function
const useAdverts: UseAdverts = () => {
  const [advert, setAdvert] = useState<Advert>();
  const [active, setActive] = useState(false);
  const timerRef = useRef<NodeJS.Timer>();

  const ga = (eventAction: string) => {
    const storedAdvert = getStoredAdvert();
    if (!storedAdvert) return;

    window.gtag('event', eventAction, {
      event_category: 'adverts',
      value: storedAdvert.id,
    });
  };

  const makeActive = () => {
    setActive((prev) => {
      if (!prev) ga('active');
      return true;
    });
  };

  const makeInactive = () => {
    setActive(false);
  };

  const clearStorage = () => {
    makeInactive();
    localStorage.removeItem('advert');
    localStorage.removeItem('advertBlocked');
    localStorage.removeItem('advertNextAppearance');
  };

  const setNextAppearance = (duration: number) => {
    const appearAt = new Date().getTime() + duration * 1000;
    localStorage.setItem('advertNextAppearance', appearAt.toString());
  };

  const close = () => {
    ga('closed');
    makeInactive();
    if (advert) setNextAppearance(advert.next_appearance);
  };

  const accept = () => {
    ga('accepted');
    makeInactive();
    localStorage.setItem('advertBlocked', 'true');
  };

  const checkAppearance = () => {
    if (blocked()) return;

    const appearanceTime = localStorage.getItem('advertNextAppearance');
    if (!appearanceTime) return;

    const appearAt = parseInt(appearanceTime, 10);
    if (new Date().getTime() >= appearAt) makeActive();
  };

  const onLoad = (response: AdvertResponse) => {
    if (!response.data.length) {
      clearStorage();
      return;
    }

    const loadedAdvert: Advert = { id: response.data[0].id, ...response.data[0].attributes };
    const storedAdvert = getStoredAdvert();

    if (storedAdvert && parseInt(storedAdvert.id, 10) !== parseInt(loadedAdvert.id, 10)) clearStorage();

    localStorage.setItem('advert', JSON.stringify(loadedAdvert));
    setAdvert(loadedAdvert);
    setNextAppearance(loadedAdvert.first_appearance);

    checkAppearance();
    timerRef.current = setInterval(checkAppearance, 1000);
  };

  const load = () => {
    fetchAPI('/adverts', { method: 'GET', body: null })
      .then(onLoad)
      .catch((v: unknown) => {
        console.log('Advert loading error', v);
      });
  };

  useEffect(() => {
    load();

    return () => {
      clearInterval(timerRef.current);
    };
  }, []);

  return {
    active,
    advert,
    close,
    accept,
  };
};

export default useAdverts;
